import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { trackEvent } from '../../../analytics';
import { facebookUrl, inviteUrl, shareText, whatsAppUrl } from '../utils';
import { CopyInviteLinkButton, InvitePreviewCard, InvitePreviewData } from './InviteParts';
import { nativeShare as appShare } from '../../../native';
import { InstagramStoryCard } from './InstagramStoryCard';

// Screen 4: bottom sheet with WhatsApp / Instagram Story / Facebook / More, the link, and the
// invitation card. Nothing is sent automatically; each option opens the app's own composer.
export function ShareInviteSheet({ invite, publicToken, refCode, onClose }: {
  invite: InvitePreviewData; publicToken: string; refCode?: string | null; onClose: () => void;
}) {
  const url = inviteUrl(publicToken, refCode);
  const text = shareText(invite, url);
  const [story, setStory] = useState(false);

  useEffect(() => {
    trackEvent('hangout_invite_shared', { step: 'sheet_opened' });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const openWhatsApp = () => {
    trackEvent('hangout_invite_shared_whatsapp');
    window.open(whatsAppUrl(text), '_blank', 'noopener');
  };
  const openFacebook = () => {
    trackEvent('hangout_invite_shared_facebook');
    window.open(facebookUrl(url), '_blank', 'noopener,width=620,height=560');
  };
  const nativeShare = async () => {
    trackEvent('hangout_invite_shared', { method: 'native' });
    if (await appShare({ title: `Night out at ${invite.venue.name}`, text: text.replace(`
${url}`, ''), url })) return;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: `Night out at ${invite.venue.name}`, text: text.replace(`\n${url}`, ''), url });
        return;
      } catch {
        return; // closed by the user
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      alert('Invite copied. Paste it into any app to share.');
    } catch {
      /* clipboard blocked; the link row below still works */
    }
  };

  return (
    <div className="hg-sheet-backdrop" onClick={onClose}>
      <section className="hg-sheet" role="dialog" aria-modal="true" aria-label="Share invite" onClick={(e) => e.stopPropagation()}>
        <div className="hg-sheet-head">
          <h2>Share Invite</h2>
          <button type="button" className="hg-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>

        <div className="hg-share-apps">
          <button type="button" className="hg-share-app" onClick={openWhatsApp}><i style={{ background: '#25D366' }}><WhatsAppIcon /></i>WhatsApp</button>
          <button type="button" className="hg-share-app" onClick={() => setStory(true)}><i style={{ background: 'radial-gradient(circle at 30% 107%,#fdf497 0%,#fd5949 45%,#d6249f 60%,#285AEB 90%)' }}><InstagramIcon /></i>Instagram Story</button>
          <button type="button" className="hg-share-app" onClick={openFacebook}><i style={{ background: '#1877F2' }}><FacebookIcon /></i>Facebook</button>
          <button type="button" className="hg-share-app" onClick={nativeShare}><i style={{ background: '#3a4250' }}><MoreHorizIcon /></i>More</button>
        </div>

        <div className="hg-section-label">Share Link</div>
        <div className="hg-link">
          <span>{url}</span>
          <CopyInviteLinkButton url={url} />
        </div>

        <div className="hg-section-label">Share Invite Card</div>
        <InvitePreviewCard invite={invite} compact>
          <a className="hg-btn hg-gold hg-sm block" href={url} target="_blank" rel="noreferrer" style={{ marginTop: 10 }}>Join on Cheers2Night</a>
        </InvitePreviewCard>

        {!story ? (
          <button type="button" className="hg-story-cta" onClick={() => setStory(true)}>
            <span className="hg-ig"><InstagramIcon /></span>
            <span><b>Create Instagram Story</b><small>Share a ready-to-post story with your invite</small></span>
            <ChevronRightIcon />
          </button>
        ) : (
          <InstagramStoryCard invite={invite} url={url} />
        )}
      </section>
    </div>
  );
}
